import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { getMonth, isSameMonth } from 'date-fns'
import { DayPicker } from 'react-day-picker'
import { enGB } from 'react-day-picker/locale'
import 'react-day-picker/style.css'

import styles from './DatePicker.module.scss'
import { Weekdays } from './components/Weekdays'
import { NextMonthButton } from './components/NextMonthButton'
import { PreviousMonthButton } from './components/PreviousMonthButton'

interface Props {
    onChange?: (date: Date) => void
    value?: Date | null
    className?: string
    disableDateFrom?: Date
    disableDateBefore?: Date
    disableFutureDates?: boolean
    highlightTodayIfSelected?: boolean
}

export const DatePicker = ({
    value = new Date(),
    onChange,
    className,
    disableFutureDates = true,
    disableDateFrom,
    disableDateBefore,
    highlightTodayIfSelected = false,
}: Props) => {
    const [selectedMonth, setSelectedMonth] = useState(value ?? new Date())

    const handleDateChange = (selected: Date) => onChange && onChange(selected)
    const handleSelectedMonthChange = (selected: Date) => {
        setSelectedMonth(selected)
    }

    const isDateDisabled = (date: Date): boolean => {
        const isOutsideMonth = !isSameMonth(date, selectedMonth)
        const isFutureDate = disableFutureDates && date > new Date()
        const isAfterMax = !!disableDateFrom && disableDateFrom < date
        const isBeforeMin = !!disableDateBefore && date < disableDateBefore
        return isOutsideMonth || isFutureDate || isAfterMax || isBeforeMin
    }

    useEffect(() => {
        if (value && getMonth(value) !== getMonth(selectedMonth)) {
            setSelectedMonth(value)
        }
    }, [value])

    return (
        <DayPicker
            required
            onSelect={handleDateChange}
            onMonthChange={handleSelectedMonthChange}
            mode="single"
            animate
            showOutsideDays
            selected={value ?? undefined}
            month={selectedMonth}
            locale={enGB}
            disabled={isDateDisabled}
            className={clsx(styles.root, className)}
            classNames={{
                caption_label: styles.label,
                day: styles.day,
                day_button: styles.dayButton,
                outside: styles.outside,
                nav: styles.nav,
                month_caption: styles.caption,
                selected: highlightTodayIfSelected ? styles.highlightTodayIfSelected : styles.daySelected,
                today: styles.today,
                disabled: styles.outside,
            }}
            components={{
                NextMonthButton: () => (
                    <NextMonthButton selectedMonth={selectedMonth} disableFutureDates={disableFutureDates} />
                ),
                PreviousMonthButton,
                Weekdays,
            }}
        />
    )
}
